import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { InstanceSize } from '@chatsdet/types';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

// Types matching the API response
export type EnvironmentTemplate = {
  id: string;
  orgId: string;
  propertyId?: string | null;
  name: string;
  status:
    | 'draft'
    | 'provisioning'
    | 'sandbox_ready'
    | 'authoring'
    | 'verifying'
    | 'verified'
    | 'failed';
  repositoryFullName: string | null;
  branch: string;
  subdirectory?: string | null;
  commitSha?: string | null;
  dockerComposeContent?: string | null;
  dockerfileContent?: string | null;
  dockerfilePath?: string | null;
  configSchema?: Array<{
    key: string;
    description: string;
    required: boolean;
    sensitive: boolean;
  }> | null;
  autoConfig?: Record<string, string> | null;
  /** Sensitive auto-config values that exist but are intentionally not returned. */
  configuredSensitiveAutoConfigKeys?: string[];
  /** User-configured default values for config schema items */
  configDefaults?: Record<string, string> | null;
  /** Sensitive defaults that are configured but intentionally not returned. */
  configuredSensitiveKeys?: string[];
  analysisSummary?: string | null;
  recommendedInstanceType?: string | null;
  /** notNull DEFAULT 'medium' in the DB — always present on API responses. */
  instanceSize: InstanceSize;
  verificationPassed?: boolean | null;
  verificationLogs?: string | null;
  lastVerifiedAt?: string | null;
  emulationConfig?: EmulationTemplateConfig | null;
  createdAt: string;
  updatedAt: string;
};

export type EmulationPluginEntry = {
  pluginId: string;
  enabled: boolean;
  config: Record<string, unknown>;
  fixture?: unknown;
};

export type EmulationTemplateConfig = {
  enabled: boolean;
  plugins: EmulationPluginEntry[];
};

type ListTemplatesResponse = {
  ok: boolean;
  data: EnvironmentTemplate[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
};

type CreateTemplatePayload = {
  name: string;
  propertyId?: string;
  repositoryFullName?: string;
  branch?: string;
  subdirectory?: string;
  githubConnectionId?: string;
  hints?: string;
  instanceSize?: InstanceSize;
};

/**
 * Hook to fetch environment templates list with optional polling
 */
export function useEnvironmentTemplates(options?: {
  status?: string;
  /**
   * Narrow to one property's templates, server-side rather than by filtering
   * the result: the list is paginated, so a client-side filter silently loses a
   * property's template once an org has enough of them.
   */
  propertyId?: string | null;
  /**
   * With `propertyId`, also list the org's property-agnostic templates. They
   * are usable by any property — and the server's own runner-target check
   * permits them — so a picker without this hides valid choices.
   */
  includeOrgWide?: boolean;
  /** Skip the request — for pickers whose property is not chosen yet. */
  enabled?: boolean;
}) {
  const query = useQuery({
    queryKey: [
      'environment-templates',
      options?.status,
      options?.propertyId,
      options?.includeOrgWide ?? false,
    ],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<ListTemplatesResponse> => {
      const params = new URLSearchParams();
      if (options?.status) params.set('status', options.status);
      if (options?.propertyId) params.set('propertyId', options.propertyId);
      if (options?.includeOrgWide) params.set('includeOrgWide', 'true');

      const res = await fetch(`${API_URL}/org/environments/templates?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch templates');
      return res.json();
    },
    // Poll every 5s if any template is in a pending state
    refetchInterval: (query) => {
      const hasPending = query.state.data?.data?.some(
        (t) => t.status === 'provisioning' || t.status === 'authoring' || t.status === 'verifying'
      );
      return hasPending ? 5000 : false;
    },
  });

  return query;
}

/**
 * Hook to fetch a single template by ID
 */
export function useEnvironmentTemplate(templateId: string | null) {
  return useQuery({
    queryKey: ['environment-template', templateId],
    queryFn: async (): Promise<{ ok: boolean; data: EnvironmentTemplate }> => {
      const res = await fetch(`${API_URL}/org/environments/templates/${templateId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch template');
      return res.json();
    },
    enabled: !!templateId,
    // Poll if in a pending state
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status;
      return status === 'draft' ||
        status === 'provisioning' ||
        status === 'sandbox_ready' ||
        status === 'authoring' ||
        status === 'verifying'
        ? 5000
        : false;
    },
  });
}

/**
 * Hook to create a new template
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateTemplatePayload) => {
      const res = await fetch(`${API_URL}/org/environments/templates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to create template');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-templates'] });
    },
  });
}

/**
 * Hook to regenerate a template
 */
export function useRegenerateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, hints }: { templateId: string; hints?: string }) => {
      const res = await fetch(`${API_URL}/org/environments/templates/${templateId}/regenerate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ hints }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to regenerate template');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-templates'] });
    },
  });
}

/**
 * Hook to delete a template
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      const res = await fetch(`${API_URL}/org/environments/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to delete template');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-templates'] });
    },
  });
}

/**
 * Hook to update template config defaults
 */
export function useUpdateTemplateConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      configDefaults,
      unsetKeys = [],
    }: {
      templateId: string;
      configDefaults: Record<string, string>;
      unsetKeys?: string[];
    }) => {
      const res = await fetch(`${API_URL}/org/environments/templates/${templateId}/config`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ configDefaults, unsetKeys }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update config');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['environment-templates'] });
      queryClient.invalidateQueries({ queryKey: ['environment-template', variables.templateId] });
    },
  });
}

/**
 * Hook to update template emulation config
 */
export function useUpdateEmulationConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      emulationConfig,
    }: {
      templateId: string;
      emulationConfig: EmulationTemplateConfig;
    }) => {
      const res = await fetch(`${API_URL}/org/environments/templates/${templateId}/emulation`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(emulationConfig),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update emulation config');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['environment-templates'] });
      queryClient.invalidateQueries({ queryKey: ['environment-template', variables.templateId] });
    },
  });
}

/**
 * Hook to update template instance size.
 */
export function useUpdateInstanceSize() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      instanceSize,
    }: {
      templateId: string;
      instanceSize: InstanceSize;
    }) => {
      const res = await fetch(`${API_URL}/org/environments/templates/${templateId}/instance-size`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ instanceSize }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update instance size');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['environment-templates'] });
      queryClient.invalidateQueries({ queryKey: ['environment-template', variables.templateId] });
    },
  });
}

/**
 * Hook to rename a template.
 */
export function useUpdateTemplateName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, name }: { templateId: string; name: string }) => {
      const res = await fetch(`${API_URL}/org/environments/templates/${templateId}/name`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to rename template');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['environment-templates'] });
      queryClient.invalidateQueries({ queryKey: ['environment-template', variables.templateId] });
    },
  });
}

/**
 * Response type for start authoring
 */
type StartAuthoringResponse = {
  ok: boolean;
  data: {
    templateId: string;
    instanceId: string;
    conversationId: string;
    status: string;
  };
};

/**
 * Hook to start interactive authoring for a template
 */
export function useStartAuthoring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      hints,
      mode,
    }: {
      templateId: string;
      hints?: string;
      mode?: 'author' | 'explore';
    }): Promise<StartAuthoringResponse> => {
      const res = await fetch(
        `${API_URL}/org/environments/templates/${templateId}/start-authoring`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ hints, mode }),
        }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to start authoring');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['environment-templates'] });
      queryClient.invalidateQueries({ queryKey: ['environment-template', variables.templateId] });
    },
  });
}
